// src/pages/ContactUsPage.jsx
import React, { useState, useEffect } from "react";
import ContactCardsSection from "../components/contact/ContactCardsSection";
import ContactMap from "../components/contact/ContactMap";
import { useLocale } from "../contexts/LocaleContext";
import { getContactUsPageData } from "../api/apiService";
import PageBanner from "../components/common/PageBanner";
import {
  flattenStrapiEntity,
  normalizeStrapiRelationArray,
} from "../utils/strapiFlatten";

const ContactUsPage = () => {
  const { locale } = useLocale();
  const [contactPageData, setContactPageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPageData = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await getContactUsPageData(locale);
        setContactPageData(data);
      } catch (err) {
        setError(err.message || "Failed to load content.");
        setContactPageData(null);
      } finally {
        setLoading(false);
      }
    };
    fetchPageData();
  }, [locale]);

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[calc(100vh-200px)]">
        <p className="text-xl text-gray-500">Loading...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto p-4 text-center">
        <p className="text-red-500 text-xl">Error: {error}</p>
      </div>
    );
  }

  if (!contactPageData) {
    const message = error
      ? error
      : `Contact Page not found or content is unavailable.`;
    return (
      <div className="container mx-auto p-4 text-center">
        <p className="text-gray-700 text-xl">{message}</p>
      </div>
    );
  }

  const banner = contactPageData.banner;
  const bannerTitle = banner?.title || "Contact Us";
  const bannerImageUrl = banner?.image?.[0]?.url;
  const bannerTitleColorIsWhite =
    banner?.titleColorIsWhite !== undefined
      ? banner.titleColorIsWhite
      : true;

  const googleMapUrl =
    contactPageData.googleMapUrl ||
    "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3689.667879178633!2d114.11756500000003!3d22.366166000000003!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3403f8eb74b90be1%3A0xd40475ed0cd71f0b!2sCAS+Systems+Limited!5e0!3m2!1szh-TW!2shk!4v1401780697928";

  const rawAddressCard =
    contactPageData.addressCard ?? contactPageData.address_card;
  let addressCards = normalizeStrapiRelationArray(rawAddressCard)
    .map(flattenStrapiEntity)
    .filter(Boolean);
  if (addressCards.length === 0 && rawAddressCard) {
    const single = flattenStrapiEntity(rawAddressCard);
    if (single?.url) addressCards = [single];
  }

  return (
    <div className="contact-us-page min-h-screen">
      {/* Banner Section */}
      <PageBanner
        title={bannerTitle}
        titleColorIsWhite={bannerTitleColorIsWhite}
        imageUrl={bannerImageUrl}
        bannerHeightClass="h-64 sm:h-72 md:h-80 lg:h-96"
      />

      {/* Main Content Section */}
      <section className="section-surface-mist w-full py-12 md:py-16 lg:py-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
          {/* Left Side - Contact Information Cards */}
          <ContactCardsSection addressCards={addressCards} />

          {/* Right Side - Google Map */}
          <ContactMap googleMapUrl={googleMapUrl} />
        </div>
      </section>
    </div>
  );
};

export default ContactUsPage;